import {
  Menu,
  MenuPopup,
  MenuTrigger,
  OPTION_PANEL_SECTION_INSET,
  OptionPanelNavRow,
  OptionPanelRow,
  Switch,
} from "@salestrak/ui";
import type { Omittable } from "@salestrak/ui";

import type { UseFiltersResult } from "#app/lib/filters/types";
import { FilterValueInput } from "./filter-value-input";
import { isDrillableSection } from "./quick-filter-page";
import { QuickDateSubmenu } from "./quick-inputs/quick-date-submenu";
import {
  writeQuickValue,
  type QuickFilterSection,
} from "./quick-inputs/quick-filter-options";
import { QuickFilterValue } from "./quick-inputs/quick-filter-value";
import { QuickOptionSubmenu } from "./quick-inputs/quick-option-submenu";
import {
  isEntityPickerSection,
  QuickEntityPicker,
} from "#app/components/filters/quick-inputs/quick-entity-picker";

type RowProps = Omittable<{
  section: QuickFilterSection;
  filters: UseFiltersResult;
}>;

// ---------------------------------------------------------------------------
// ONE quick filter as ONE row of the panel's settings list.
//
// The panel is an `OptionPanel` list, the same grammar its sibling
// Customize view pages are built from — this page is reached by drilling into
// that panel, so it may not change language on arrival. Every row reads
// `icon label … value ›` and states its value AS the value: three assignees are
// three avatars, two follow-up states are their two coloured glyphs
// (`QuickFilterValue`).
//
// The row's shape is chosen by what the filter IS and never by how many values
// it has. Two shapes are the same on every surface: a lone flag is a switch,
// because a proposition to agree with has nothing to open at all; and a filter
// with no enumerable pool — free text, a number, a bare date — has no list, so
// its editor sits on the row's trailing edge. A set of values opens a list, and
// WHERE it opens is the one thing the two exported rows differ on:
//
//   QuickFilterPopupRow   beside the panel, on hover        (desktop)
//   QuickFilterRow        a page pushed over the list       (mobile)
// ---------------------------------------------------------------------------

/** One flag. The row IS the proposition, so the switch sits on its edge and
 *  there is no list of one to open. */
function FlagRow({ section, filters }: RowProps) {
  const { def, options } = section;
  const [flag] = options;
  if (!flag) return null;
  const value = filters.quickValues[def.field];
  return (
    <OptionPanelRow label={def.label} icon={def.icon}>
      <Switch
        size="sm"
        checked={flag.selected(value)}
        onCheckedChange={() =>
          filters.setQuickValue(def.field, flag.toggle(value))
        }
      />
    </OptionPanelRow>
  );
}

/** A filter with no enumerable pool: its editor lives on the row itself. */
function FreeInputRow({ section, filters }: RowProps) {
  const { def } = section;
  return (
    <OptionPanelRow label={def.label} icon={def.icon}>
      <FilterValueInput
        fieldType={def.type}
        value={filters.quickValues[def.field]}
        onChange={(next) => filters.setQuickValue(def.field, next)}
      />
    </OptionPanelRow>
  );
}

type QuickFilterPopupRowProps = RowProps &
  Omittable<{
    /** This filter's values are open beside the panel right now. */
    open: boolean;
    /** Base UI reports every open and close here — a hover, a press, an
     *  outside press, Escape. The list owns which ONE row is open. */
    onOpenChange: (open: boolean) => void;
  }>;

/**
 * The desktop row: its values open in a popup BESIDE the panel on HOVER, the
 * way the toolbar menu's rows open their submenus — sliding from Assignee to
 * Stage swaps the popup with no press at all, and there is nothing to go Back
 * from. A press opens it too, for a pointer that does not hover.
 *
 * A real Base UI menu per row, never a popover opened on `mouseenter`: the
 * trigger's hover uses a safe polygon, which is what lets the pointer travel
 * diagonally from the row into the popup without the rows it crosses stealing
 * it, and a rest delay, so a pointer passing through the list opens nothing.
 * `modal={false}` because a modal menu drops a backdrop over the panel while
 * it is open, and the next row could not be hovered through it.
 *
 * The popup's body is the same one the toolbar menu's submenus render
 * (`QuickOptionSubmenu`, `QuickDateSubmenu`), so a value picked here and a
 * value picked from the toolbar chip come from the one options index.
 */
export function QuickFilterPopupRow({
  section,
  filters,
  open,
  onOpenChange,
}: QuickFilterPopupRowProps) {
  const { def, options } = section;

  if (def.type === "more-options") {
    return <FlagRow section={section} filters={filters} />;
  }
  if (options.length === 0) {
    return <FreeInputRow section={section} filters={filters} />;
  }

  const value = filters.quickValues[def.field];
  const setValue = (next: unknown) => writeQuickValue(filters, section, next);
  const entityPicker = isEntityPickerSection(section);

  return (
    <Menu open={open} onOpenChange={onOpenChange} modal={false}>
      <MenuTrigger
        openOnHover
        render={
          <OptionPanelNavRow
            label={def.label}
            icon={def.icon}
            value={<QuickFilterValue section={section} value={value} />}
          />
        }
      />
      {/* `inline-end` is the reading direction a submenu opens in; against a
          panel pinned to the pane's end edge the positioner flips it, so the
          popup lands over the view. The offset carries the popup's edge out to
          the panel's edge rather than the row's. The first value is primed as
          the row a press would act on — a look only, since the pointer chose
          the ROW. */}
      <MenuPopup
        side="inline-end"
        align="start"
        sideOffset={OPTION_PANEL_SECTION_INSET}
        width="panel"
        autoHighlight={!entityPicker}
        padding={entityPicker ? "none" : undefined}
      >
        {entityPicker ? (
          <QuickEntityPicker section={section} value={value} onChange={setValue} />
        ) : def.type === "last-updated" ? (
          <QuickDateSubmenu
            options={options}
            value={value}
            onChange={setValue}
          />
        ) : (
          <QuickOptionSubmenu
            options={options}
            value={value}
            onChange={setValue}
            label={def.label}
          />
        )}
      </MenuPopup>
    </Menu>
  );
}

type QuickFilterRowProps = RowProps &
  Omittable<{
    /** Push this filter's page over the list. */
    onOpen: (field: string) => void;
  }>;

/**
 * The mobile row: a press pushes the filter's values as a PAGE over the list
 * (`QuickFilterPage`, in the drawer's `DrawerPages` stack). Touch has no
 * hover, and a popup has nowhere to open beside a drawer that reaches both
 * screen edges; a list disclosed under the row would push the other filters
 * off the surface the reader opened to read, and a roster of forty has no
 * height to disclose into at all.
 *
 * A date window drills the same way as a pool of members — its page is the
 * preset ramp plus the custom range — so every row that opens anything opens
 * it in the one direction. The row's chevron points where the page comes
 * from; `expanded` is not set, because nothing opens in place.
 */
export function QuickFilterRow({ section, filters, onOpen }: QuickFilterRowProps) {
  const { def } = section;

  if (def.type === "more-options") {
    return <FlagRow section={section} filters={filters} />;
  }
  if (!isDrillableSection(section)) {
    return <FreeInputRow section={section} filters={filters} />;
  }

  return (
    <OptionPanelNavRow
      label={def.label}
      icon={def.icon}
      value={
        <QuickFilterValue
          section={section}
          value={filters.quickValues[def.field]}
        />
      }
      onClick={() => onOpen(def.field)}
    />
  );
}
